import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Switch from "react-switch";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import { database } from "../../database/firebase";
import icon1 from "../../img/icon1.png";
import icon2 from "../../img/icon2.png";
import icon3 from "../../img/icon3.png";
import icon4 from "../../img/icon4.png";
import icon5 from "../../img/icon5.png";
import icon6 from "../../img/icon6.png";
import icon7 from "../../img/icon7.png";
import icon8 from "../../img/icon8.png";
import MonkeyBackdrop from "../../components/MonkeyBackdrop";

import "./host.css";

const LOBBY_COLLECTION = "lobby";
const LOBBY_USERDATA = "userdata";

const PLAYER_AVATAR_ICONS = [icon1, icon2, icon3, icon4, icon5, icon6, icon7, icon8];

function characterHashFromUserId(userId) {
  let h = 0;
  const s = String(userId);
  for (let i = 0; i < s.length; i += 1) {
    h = Math.imul(31, h) + s.charCodeAt(i) | 0;
  }
  return Math.abs(h);
}

function characterIndexForUserId(userId) {
  return characterHashFromUserId(userId) % PLAYER_AVATAR_ICONS.length;
}

function readExplicitCharacterIndex(data) {
  const count = PLAYER_AVATAR_ICONS.length;
  const legacy = String(data?.character ?? "").trim();
  let legacyIndex = null;
  if (legacy) {
    const m = legacy.match(/(\d+)$/);
    if (m) {
      const legacyOneBased = Number(m[1]);
      if (Number.isFinite(legacyOneBased) && legacyOneBased >= 1) {
        legacyIndex = (Math.floor(legacyOneBased) - 1) % count;
      }
    }
  }

  const direct = Number(data?.characterIndex);
  if (Number.isFinite(direct) && direct >= 0) {
    const directIndex = Math.floor(direct) % count;
    if (Number.isFinite(legacyIndex) && legacyIndex >= 0) {
      if (directIndex === legacyIndex) return directIndex;
      if (directIndex - 1 === legacyIndex) return legacyIndex;
      return legacyIndex;
    }
    return directIndex;
  }

  if (Number.isFinite(legacyIndex) && legacyIndex >= 0) {
    return legacyIndex;
  }

  return null;
}

function normalizeCharacterIndex(data, userId) {
  const explicit = readExplicitCharacterIndex(data);
  if (Number.isFinite(explicit) && explicit >= 0) {
    return explicit % PLAYER_AVATAR_ICONS.length;
  }

  return characterIndexForUserId(userId);
}

function avatarIconForCharacterIndex(characterIndex) {
  const safe = Number.isFinite(characterIndex) ? Math.floor(characterIndex) : 0;
  return PLAYER_AVATAR_ICONS[((safe % PLAYER_AVATAR_ICONS.length) + PLAYER_AVATAR_ICONS.length) % PLAYER_AVATAR_ICONS.length];
}

const EXTRAMILE_GUEST_JOIN =
  (process.env.REACT_APP_EXTRAMILE_GUEST_JOIN_URL || "").trim() ||
  "https://extramileplay.com/join/guest";
const EXTRAMILE_SIGNUP =
  (process.env.REACT_APP_EXTRAMILE_SIGNUP_URL || "").trim() ||
  "https://extramileplay.com/singin-signup";

function readTruthLobby() {
  try {
    const raw = sessionStorage.getItem("truthLobby");
    if (!raw) return null;
    const data = JSON.parse(raw);
    console.log("data readTruthLobby",data);
    return data && typeof data === "object" ? data : null;
  } catch {
    return null;
  }
}

function readUserFromStorage() {
  try {
    const raw = sessionStorage.getItem("userData");
    if (!raw) return null;
    const u = JSON.parse(raw);
    console.log("userdata",u);
    return u && typeof u === "object" ? u : null;
  } catch {
    return null;
  }
}

function buildExtramileJoinUrl(isGuest, gameId, sessionId) {
  const base = (isGuest ? EXTRAMILE_GUEST_JOIN : EXTRAMILE_SIGNUP).replace(/\/$/, "");
  const u = new URL(base);
  u.searchParams.set("gameId", String(gameId ?? ""));
  u.searchParams.set("sessionId", String(sessionId ?? ""));
  return u.toString();
}

/** Keep ExtraMile join URL; append lobby id + share flags for this game. */
function appendLobbyShareFlagsToJoinUrl(baseUrl, lobbyId) {
  const base = String(baseUrl ?? "").trim();
  const id = String(lobbyId ?? "").trim();
  if (!base) return "";
  if (!id) return base;
  try {
    const u = new URL(base);
    u.searchParams.set("lobby", id);
    u.searchParams.set("fromShare", "1");
    u.searchParams.set("shared", "1");
    return u.toString();
  } catch {
    return base;
  }
}

function parseShareFlagsAndLobbyName(params) {
  const truthy = (v) =>
    ["1", "true", "yes"].includes(String(v ?? "").toLowerCase());
  const fromShare =
    truthy(params.get("fromShare")) || truthy(params.get("shared"));
  const lobbyName = String(
    params.get("lobbyName") ?? params.get("room") ?? params.get("roomName") ?? ""
  ).trim();
  return {
    fromShare,
    lobbyName: lobbyName.slice(0, 80),
  };
}

function computeFillSeconds(roomLike) {
  const base = Number(roomLike?.baseMinutes ?? 2);
  const per = Number(roomLike?.perPlayerSeconds ?? 20);
  const players = Number(roomLike?.maxPlayers ?? 5);
  const n = Number.isFinite(players) && players > 0 ? players : 5;
  const sec =
    Math.round((Number.isFinite(base) ? base : 2) * 60 + (Number.isFinite(per) ? per : 20) * n);
  return Math.max(90, sec);
}

function assignUniqueCharacterIndexes(docs) {
  const count = PLAYER_AVATAR_ICONS.length;
  const entries = [];

  for (const d of docs) {
    const data = d.data() || {};
    const id = String(data.userId ?? d.id ?? "").trim();
    if (!id) continue;
    const hasLive = Object.prototype.hasOwnProperty.call(data, "live");
    if (hasLive && data.live !== true) continue;
    entries.push({
      id,
      explicitIndex: readExplicitCharacterIndex(data),
      preferredIndex: normalizeCharacterIndex(data, id),
    });
  }

  entries.sort((a, b) => a.id.localeCompare(b.id));
  const assignedById = new Map();
  const used = new Set();

  for (const entry of entries) {
    const idx = entry.explicitIndex;
    if (!Number.isFinite(idx)) continue;
    const safe = ((Math.floor(idx) % count) + count) % count;
    if (used.has(safe)) continue;
    assignedById.set(entry.id, safe);
    used.add(safe);
  }

  for (const entry of entries) {
    if (assignedById.has(entry.id)) continue;

    const preferred = ((Math.floor(entry.preferredIndex) % count) + count) % count;
    let chosen = preferred;

    if (used.size < count) {
      while (used.has(chosen)) {
        chosen = (chosen + 1) % count;
      }
      used.add(chosen);
    }

    assignedById.set(entry.id, chosen);
  }

  return assignedById;
}

function normalizePlayersFromSnapshot(docs, assignedById) {
  const byId = new Map();

  for (const d of docs) {
    const data = d.data() || {};
    const id = String(data.userId ?? d.id ?? "").trim();
    if (!id) continue;
    const hasLive = Object.prototype.hasOwnProperty.call(data, "live");
    if (hasLive && data.live !== true) continue;
    const photoURL =
      data.photoURL || data.photoUrl || data.avatarUrl || data.avatar || "";
    const characterIndex = assignedById.get(id) ?? normalizeCharacterIndex(data, id);
    byId.set(id, {
      userId: id,
      name: String(data.name ?? "").trim(),
      email: String(data.email ?? "").trim(),
      photoURL: typeof photoURL === "string" ? photoURL.trim() : "",
      character: String(data.character ?? `character_${characterIndex + 1}`),
      characterIndex,
    });
  }

  return [...byId.values()].sort((a, b) => {
    const la = (a.name || a.email || a.userId).toLowerCase();
    const lb = (b.name || b.email || b.userId).toLowerCase();
    return la.localeCompare(lb);
  });
}

function Host({ updateHeader }) {
  
      useEffect(() => {
        updateHeader({
          backButtonRedirect: "/userpage",
          // ...(companyLogo ? { imageUrl: companyLogo } : {}),
        });
      }, []);
    
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [lobby, setLobby] = useState(() => readTruthLobby());
  const [hostUser, setHostUser] = useState(() => readUserFromStorage());
  const [players, setPlayers] = useState([]);
  const [autoplay, setAutoplay] = useState(false);
  const [copyHint, setCopyHint] = useState("");
  const [qrFailed, setQrFailed] = useState(false);
  const [brokenPhotoByUserId, setBrokenPhotoByUserId] = useState({});
  const [isCopying, setIsCopying] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  /** When true, do not delete session user doc (e.g. navigating to /game). */
  const skipSessionPresenceDeleteRef = useRef(false);
  const sessionPresenceTargetRef = useRef({ sessionId: "", userId: "" });

  useEffect(() => {
    setLobby(readTruthLobby());
    setHostUser(readUserFromStorage());
  }, []);

  /** If /host is opened with share query flags, sync lobby display name into sessionStorage. */
  useEffect(() => {
    const { fromShare, lobbyName } = parseShareFlagsAndLobbyName(searchParams);
    if (!fromShare || !lobbyName) return;
    try {
      const prev = readTruthLobby() || {};
      const next = {
        ...prev,
        name: lobbyName,
        fromShareableLink: true,
      };
      sessionStorage.setItem("truthLobby", JSON.stringify(next));
      setLobby(next);
    } catch {
      /* ignore */
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("fromShare");
    nextParams.delete("shared");
    nextParams.delete("lobbyName");
    nextParams.delete("room");
    nextParams.delete("roomName");
    if ([...nextParams.keys()].length === 0) {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  
  const isGuest = useMemo(() => {
    const t = String(hostUser?.user_type ?? hostUser?.userType ?? "").toLowerCase();
    if (t === "guest") return true;
    const src = String(hostUser?.source ?? hostUser?.roles ?? "").toUpperCase();
    return src === "GUEST_USER" || src === "GUEST";
  }, [hostUser]);

  const sessionIdForRoom = useMemo(
    () =>
      String(lobby?.sessionId ?? hostUser?.sessionId ?? "")
        .trim(),
    [lobby?.sessionId, hostUser?.sessionId]
  );

  useEffect(() => {
    sessionPresenceTargetRef.current = {
      sessionId: String(sessionIdForRoom ?? "").trim(),
      userId: String(hostUser?.userId ?? hostUser?.userid ?? "").trim(),
    };
  }, [sessionIdForRoom, hostUser?.userId, hostUser?.userid]);

  /** Remove this user from `sessions/.../users` when leaving host (tab close / navigate away), not when starting the game. */
  useEffect(() => {
    const removeLiveSessionUser = () => {
      const { sessionId, userId } = sessionPresenceTargetRef.current;
      if (!sessionId || !userId) return;
      deleteDoc(doc(database, "sessions", sessionId, "users", userId)).catch(
        (err) => {
          console.warn("Session presence cleanup:", err);
        }
      );
    };

    const onPageHide = (e) => {
      if (skipSessionPresenceDeleteRef.current) return;
      if (e.persisted === true) return;
      removeLiveSessionUser();
    };

    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      if (!skipSessionPresenceDeleteRef.current) {
        removeLiveSessionUser();
      }
      skipSessionPresenceDeleteRef.current = false;
    };
  }, []);

  const extramileJoinUrl = useMemo(() => {
    const sessionId = sessionIdForRoom;
    const gameId = String(hostUser?.gameId ?? "").trim();
    if (!sessionId) return "";
    return buildExtramileJoinUrl(isGuest, gameId, sessionId);
  }, [sessionIdForRoom, hostUser?.gameId, isGuest]);

  const scanJoinUrl = useMemo(
    () => appendLobbyShareFlagsToJoinUrl(extramileJoinUrl, lobby?.lobbyId),
    [extramileJoinUrl, lobby?.lobbyId]
  );

  const roomIdForLobby = useMemo(
    () => String(lobby?.lobbyId ?? "").trim(),
    [lobby?.lobbyId]
  );

  useEffect(() => {
    if (!sessionIdForRoom || !roomIdForLobby) {
      setPlayers([]);
      return undefined;
    }
    const coll = collection(
      database,
      LOBBY_COLLECTION,
      sessionIdForRoom,
      "rooms",
      roomIdForLobby,
      LOBBY_USERDATA
    );
    const unsub = onSnapshot(
      coll,
      (snap) => {
        const assignedById = assignUniqueCharacterIndexes(snap.docs);
        setPlayers(normalizePlayersFromSnapshot(snap.docs, assignedById));
        for (const d of snap.docs) {
          const data = d.data() || {};
          const id = String(data.userId ?? d.id ?? "").trim();
          if (!id) continue;
          const hasLive = Object.prototype.hasOwnProperty.call(data, "live");
          if (hasLive && data.live !== true) continue;

          const characterIndex =
            assignedById.get(id) ?? normalizeCharacterIndex(data, id);
          const expectedCharacter = `character_${characterIndex + 1}`;
          const currentCharacter = String(data.character ?? "").trim();
          const currentCharacterIndex = readExplicitCharacterIndex(data);

          if (
            currentCharacter === expectedCharacter &&
            Number.isFinite(currentCharacterIndex) &&
            currentCharacterIndex === characterIndex
          ) {
            continue;
          }

          setDoc(
            doc(
              database,
              LOBBY_COLLECTION,
              sessionIdForRoom,
              "rooms",
              roomIdForLobby,
              LOBBY_USERDATA,
              d.id
            ),
            {
              character: expectedCharacter,
              characterIndex,
            },
            { merge: true }
          ).catch((err) => {
            console.warn("Host character sync:", err);
          });
        }
      },
      (e) => {
        console.error("Host lobby players listener:", e);
        setPlayers([]);
      }
    );
    return () => unsub();
  }, [sessionIdForRoom, roomIdForLobby]);

  const maxPlayers = useMemo(() => {
    const n = Number(lobby?.maxPlayers);
    if (Number.isFinite(n) && n >= 1) return Math.floor(n);
    return 10;
  }, [lobby?.maxPlayers]);
  const lobbyTitle = lobby?.name?.trim() || "Lobby";
  const playerCount = players.length;
  const canStartGame =
    Boolean(lobby) && playerCount === maxPlayers && maxPlayers >= 1;

  const qrSrc = useMemo(() => {
    if (!scanJoinUrl) return "";
    return `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=2&data=${encodeURIComponent(scanJoinUrl)}`;
  }, [scanJoinUrl]);

  useEffect(() => {
    setQrFailed(false);
    setBrokenPhotoByUserId({});
  }, [scanJoinUrl, sessionIdForRoom]);

  const handleCopy = useCallback(async () => {
    const text = scanJoinUrl;
    setCopyHint("");
    if (!text) {
      setCopyHint("Join link not available yet");
      window.setTimeout(() => setCopyHint(""), 2200);
      return;
    }
    
    setIsCopying(true);
    try {
      await navigator.clipboard.writeText(text);
      setCopyHint("Copied!");
      setShowCopySuccess(true);
      window.setTimeout(() => {
        setCopyHint("");
        setShowCopySuccess(false);
      }, 2200);
    } catch {
      setCopyHint("Copy manually from link above");
    } finally {
      window.setTimeout(() => setIsCopying(false), 300);
    }
  }, [scanJoinUrl]);

  const toggleFullscreen = useCallback(() => {
    const el = document.documentElement;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  }, []);

  const handleStart = useCallback(async () => {
    alert("handleStart");
    if (!canStartGame) return;
    
    setIsStarting(true);
    
    const t = readTruthLobby();
    console.log("T---------",t);
    const sid = String(t?.sessionId ?? lobby?.sessionId ?? "").trim();
    const roomId = String(t?.lobbyId ?? lobby?.lobbyId ?? "").trim();
    if (!sid || !roomId) {
      setCopyHint("Missing session or room id. Open lobby setup first.");
      window.setTimeout(() => {
        setCopyHint("");
        setIsStarting(false);
      }, 3200);
      return;
    }
    try {
      const base = t || lobby || {};
      const fillDurationSeconds = Number(base?.perPlayerSeconds ?? 80);
      const nowMs = Date.now();
      const fillEndsMs = nowMs + fillDurationSeconds * 1000;
      await setDoc(
        doc(database, LOBBY_COLLECTION, sid, "rooms", roomId),
        {
          status: "fill",
          statusUpdatedAt: serverTimestamp(),
          fillStartedAt: Timestamp.fromMillis(nowMs),
          fillEndsAt: Timestamp.fromMillis(fillEndsMs),
          fillDurationSeconds,
          gameFinished: false,
          gameStatementOrder: [],
          gameRoundIdx: 0,
          gameSubjectUserId: "",
          gameGuessEpoch: 1,
          gameLastScoredKey: "",
          userCount: 0,
        },
        { merge: true }
      );
      try {
        const next = { ...base, sessionId: sid, lobbyId: roomId, status: "fill" };
        sessionStorage.setItem("truthLobby", JSON.stringify(next));
        setLobby(next);
      } catch {
        /* ignore */
      }
      
      // Add a small delay for animation effect
      await new Promise(resolve => setTimeout(resolve, 600));
      skipSessionPresenceDeleteRef.current = true;
      navigate("/waiting");
    } catch (e) {
      console.error("host room status fill:", e);
      setCopyHint(
        String(e?.message || "").trim() ||
          "Could not update room status. Check Firestore rules."
      );
      window.setTimeout(() => {
        setCopyHint("");
        setIsStarting(false);
      }, 4000);
      return;
    }
  }, [canStartGame, lobby, navigate]);

  return (
    <main className="host">
      <MonkeyBackdrop />
      <div className="host__decor" aria-hidden>
        <span>🐵</span>
        <span>🐵</span>
      </div>

      {!lobby ? (
        <p className="host__warn">
          No lobby saved yet.{" "}
          <Link to="/userpage">Open setup</Link> to create one, then return here.
        </p>
      ) : null}

      <header className="host__head">
        <h1 className="host__title">Truth &amp; Trick 👁️</h1>
        {/* <div className="host__badge-row">
          <span className="host__badge-emoji" aria-hidden>
            🧊👉
          </span>
          <span className="host__badge">Ice Breaker</span>
        </div> */}
        {/* {lobby ? (
          <p className="host__lobby-name">
            Lobby: <strong>{lobbyTitle}</strong>
          </p>
        ) : null} */}
      </header>

      <div className="host__grid">
        <section className="host__panel host__panel--join" aria-label="Join instructions">
          <div className="host__join-col">
            <span className="host__join-label">Scan to join</span>
            {!scanJoinUrl ? (
              <div className="host__qr-fallback">
                Add a lobby and sign in so <code>sessionId</code> and <code>gameId</code> are available for the join link.
              </div>
            ) : !qrFailed ? (
              <img
                className="host__qr"
                src={qrSrc}
                alt="QR code to open ExtraMile join or sign-in"
                onError={() => setQrFailed(true)}
              />
            ) : (
              <div className="host__qr-fallback">
                QR image failed to load. Use the copy button or open the join URL manually.
              </div>
            )}
            <button 
              type="button" 
              className={`begin-play-btn begin-play-btn--secondary ${isCopying ? 'begin-play-btn--copying' : ''} ${showCopySuccess ? 'begin-play-btn--copy-success' : ''}`} 
              onClick={handleCopy}
              disabled={isCopying}
            >
              <span className="begin-play-btn__content">
                {isCopying ? (
                  <>
                    <span className="begin-play-btn__spinner"></span>
                    Copying...
                  </>
                ) : showCopySuccess ? (
                  <>
                    <span className="begin-play-btn__check">✓</span>
                    Copied!
                  </>
                ) : (
                  <>
                    <span className="begin-play-btn__icon">📋</span>
                    Copy Shareable Link
                  </>
                )}
              </span>
            </button>
            <span className={`host__copy-hint ${showCopySuccess ? 'host__copy-hint--success' : ''}`} role="status">
              {copyHint}
            </span>
          </div>
        </section>

        <section className="host__panel host__panel--players" aria-label="Players">
          <div className="host__panel-head">
            <span className="host__players-label">Players</span>
            <span className="host__players-count">
              {playerCount} / {maxPlayers} Players
            </span>
          </div>
          <div className="host__players-body">
            {playerCount === 0 ? (
              <div className="host__waiting">Waiting for players!</div>
            ) : (
              <ul className="host__players-grid">
                {players.map((p, index) => {
                  const label = p.name || p.email || p.userId;
                  const fallbackIcon = avatarIconForCharacterIndex(p.characterIndex);
                  const stagger = `${index * 0.08}s`;
                  return (
                    <li
                      key={p.userId}
                      className="host__player"
                      style={{ "--host-stagger": stagger }}
                    >
                      <div className="host__player-avatar-wrap">
                        <img
                          className="host__player-avatar host__player-avatar--img host__player-avatar--icon"
                          src={fallbackIcon}
                          alt=""
                        />
                      </div>
                      <span className="host__player-name" title={label}>
                        {label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>
      </div>

      <div className="host__start-wrap">
        <button
          type="button"
          className={`begin-play-btn ${isStarting ? 'begin-play-btn--starting' : ''}`}
          onClick={handleStart}
          disabled={!lobby || !canStartGame || isStarting}
          title={
            lobby && canStartGame && !isStarting
              ? "Start fill phase for everyone"
              : lobby
                ? isStarting
                  ? "Starting game..."
                  : `Waiting for all players (${playerCount}/${maxPlayers})`
                : "Create a lobby on setup page first."
          }
        >
          <span className="begin-play-btn__content">
            {isStarting ? (
              <>
                <span className="begin-play-btn__spinner"></span>
                Starting...
              </>
            ) : (
              <>
                <span className="begin-play-btn__icon">🚀</span>
                Let's Begin
              </>
            )}
          </span>
        </button>
        {lobby && !canStartGame ? (
          <p className="host__start-hint" role="status">
            {playerCount < maxPlayers
              ? `${playerCount} / ${maxPlayers} players connected. Play unlocks when everyone joins.`
              : `Too many players connected (${playerCount} / ${maxPlayers}).`}
          </p>
        ) : null}
      </div>

    </main>
  );
}

export default Host;

